import React, { useEffect, useMemo, useRef } from 'react';
import DataGrid, { Column, Editing, Lookup } from 'devextreme-react/data-grid';
import { Item as TabItem } from "devextreme-react/tab-panel";
import Form, { FormRef, GroupItem, Label, SimpleItem } from "devextreme-react/form";
import IPromotion from "../../models/IPromotion";
import IGestionPromotion from "../../models/IGestionPromotion";
import { FormListBase, FormListBaseGrid, FormListBaseTab, FormListBaseToolbar } from '../base';
import useCustomDataSource, { IFormBaseDataSourceOptions, Mode } from "../../hooks/useCustomDataSource";
import FormSysteme from '../../components/forms/form-systeme';
import IPromotionCondition from '../../models/IPromotionCondition';
import useLookUpBase from '../../components/selectbox/LookupBase';
import { NumberBox } from 'devextreme-react';
import GestionCatalogueProduits from '../gestion-catalogue-produits/gestion-catalogue-produits';

export default function GestionPromotions() {
  const promotionDataSourceOption: IFormBaseDataSourceOptions<IGestionPromotion> = useMemo(() => ({
    path: "promotion",
    customReadByIdPath: "promotion/{key}/gestion",
    customSauvegarderPath: "promotion/save",
    primaryKey: "idPromotion",
  }), []);
  const promotionFormDataSource = useCustomDataSource<IPromotion, IGestionPromotion>(promotionDataSourceOption);

  // const promotionFormDataSource = useCustomDataSource<IPromotion, IPromotion>({ path: "promotion", primaryKey: "idPromotion" });
  const { selectedDataRow: selectedPromotion, setMainForm, setSelectedDataRow, mode, setMode, isFieldModifier, setIsFieldModifier, onFieldDataChanged } = promotionFormDataSource;
  const formRef = useRef<FormRef>(null);
  const conditionsOptions = useMemo<IFormBaseDataSourceOptions>(() => {
    return { path: "promotionCondition", primaryKey: "idPromotionCondition" }
  }, []);
  const conditionsDataSource = useCustomDataSource<IPromotionCondition, IPromotionCondition>(conditionsOptions);
  const { editorOptions: clientsEditorOption } = useLookUpBase({ isModeModifier: mode !== Mode.Lecture, controllerName: 'client', showClear: mode !== Mode.Lecture, titre: "Gestion des clients" });
  const { editorOptions: typePaiementEditorOptions } = useLookUpBase({ isModeModifier: mode !== Mode.Lecture, controllerName: "typepaiement", showClear: mode !== Mode.Lecture });
  const { editorOptions: categoriesClientEditorOption } = useLookUpBase({ isModeModifier: mode !== Mode.Lecture, controllerName: 'categorieclient', showClear: mode !== Mode.Lecture, titre: "Gestion des catégories de clients" });
  const { editorOptions: catalogueProduitsEditorOption, PopupGestion: PopupGestionCatalogueProduits } = useLookUpBase({ isModeModifier: mode !== Mode.Lecture, controllerName: 'catalogue', showClear: mode !== Mode.Lecture, titre: "Catalogue de produits" });

  const dataGridFilterValue = useMemo(() => ["isDeleted", "anyof", [null, false]], []);

  const { quantiteEditorOptions } = useMemo(() => {
    return {
      quantiteEditorOptions: {
        format: { type: 'currency', precision: 2 },
      }
    };
  }, []);

  const types = [
    { value: 'CA', display: "Catégorie de client" },
    { value: 'C', display: "Client" },
    { value: 'EC', display: "Équipement du client" },
    { value: 'NJ', display: "Nombre de jours" },
    { value: 'NP', display: "Nombre de personnes" },
    { value: 'NR', display: "Nombre de réservations" },
    { value: 'P', display: "Produit" },
    { value: 'S', display: "Secteur" },
    { value: 'TU', display: "Type d'unité" },
    { value: 'U', display: "Unité" }
  ];

  const options_types = [
    { value: 1, text: "Catégorie de produit" },
    { value: 2, text: "Produit" },
    { value: 3, text: "Séjour complet" },
    { value: 4, text: "Tous" },
    { value: 5, text: "Type d'unités" },
    { value: 6, text: "Unité" },
  ];
  const typeRabaisOptions = useMemo(() => {
    return {
      items: options_types,
      valueExpr: 'value',
      displayExpr: 'text',
      defaultValue: options_types[0].value,
      maxLength: 2
    };
  }, [options_types]);


  const typeEditorOptions = useMemo(() => {
    return {
      items: types,
      valueExpr: 'value',
      displayExpr: 'display',
      defaultValue: types[0].value,
      maxLength: 2
    };
  }, [types]);

  // const typeEditorOptionsWithDataSource = useMemo(() => {
  //   return {
  //     ...typeEditorOptions,
  //     dataSource: [categoriesClientEditorOption]
  //   };
  // }, [typeEditorOptions]);

  const { maxUtilisationOptions, actifOptions, jumelableOptions, noteOptions, disponibleEnLigneOptions, automatiqueOptions, explicationOptions, explicationFrOptions, conditionEditorOptions } = useMemo(() => ({
    maxUtilisationOptions: { min: 0, showSpinButtons: true },
    actifOptions: { text: "Actif" },
    jumelableOptions: { text: "Jumelable" },
    noteOptions: { height: 300 },
    disponibleEnLigneOptions: { text: "Disponible en ligne" },
    automatiqueOptions: { text: "Automatique" },
    explicationOptions: { height: 300 },
    explicationFrOptions: { height: 30 },
    conditionEditorOptions: {
      items: ['Et', 'Ou']
    }
  }), []);

  useEffect(() => {
    setMainForm(formRef.current?.instance());
  })

  useEffect(() => {
    if (mode !== Mode.Lecture)
      formRef.current?.instance().getEditor("code")?.focus();
  }, [mode !== Mode.Lecture])


  const conditionalColumn = useMemo(() => {
    if (mode === Mode.Lecture)
      return <Column dataType="string" caption="Type" />

    const { defaultValue } = typeEditorOptions;
    switch (defaultValue) {
      case 'CA':
      case 'C':
      case 'EC':
      case 'NJ':
      case 'NP':
      case 'NR':
      case 'P':
      case 'S':
      case 'TU':
      case 'U':
        return (
          <Column dataField="idCategorieClient" dataType="string" alignment="left" caption={"Type"}>
            <Lookup {...categoriesClientEditorOption} />
          </Column>
        )
      default:
        return <Column dataType="string" caption="Type" />;
    }
  }, [mode, typeEditorOptions, categoriesClientEditorOption]);

  const formatCurrency = (value: number) => {
    return value?.toLocaleString('fr-FR', { minimumFractionDigits: 2 }) + ' $';
  };

  const formatValue = (value: number) => {
    const formattedValue = value?.toLocaleString('fr-FR', { minimumFractionDigits: 2 });
    return `${formattedValue} ${selectedPromotion.rabaisFixe === true ? '%' : '$'}`;
  };

  const toggleSymbol = () => {
    if (mode === Mode.Lecture) return
    setSelectedDataRow((prev: any) => {
      return (
        { ...prev, 'rabaisFixe': !prev.rabaisFixe }
      )
    })
  };

  return (
    <FormListBase data={promotionFormDataSource}>
      <FormListBaseGrid dataSource={promotionFormDataSource.dataSource}
        onFocusedRowChanged={() => {
          if (mode === Mode.Modifier) {
            setMode(Mode.Lecture)
          }
        }}
      >
        <Column key={"grid-col-description"} dataField={"description"} caption="Description" allowHiding={false} />
        <Column key={"grid-col-code"} dataField={"code"} caption="Code" allowHiding={false} />
      </FormListBaseGrid>
      <FormListBaseToolbar headerText={`${selectedPromotion?.description || ''} `} sauvegarderOptions={{ disabled: !isFieldModifier }} annulerOptions={{ disabled: !isFieldModifier }} effacerOptions={{ disabled: !isFieldModifier }} />
      <FormListBaseTab>
        <TabItem title="Informations">
          <Form ref={formRef} readOnly={mode === Mode.Lecture} alignItemLabelsInAllGroups={true} formData={selectedPromotion || {} as IPromotion} className={"forms"} labelLocation={"left"} showColonAfterLabel={true} onFieldDataChanged={onFieldDataChanged}>
            <GroupItem colCount={6}>
              <GroupItem colSpan={2}>
                <GroupItem colCount={2}>
                  <SimpleItem key={"frm-code"} dataField={"code"} />
                  <SimpleItem key={"frm-actif"} dataField={"actif"} editorType={"dxCheckBox"}
                    editorOptions={actifOptions}
                  >
                    <Label visible={false} />
                  </SimpleItem>
                </GroupItem>
                <GroupItem alignItemLabels={false}>
                  <SimpleItem key={"frm-description"} dataField={"description"} />
                  <SimpleItem key={"frm-max-utilisation"} editorType={"dxNumberBox"}
                    editorOptions={maxUtilisationOptions}
                  >
                    <Label text={"Nombre maximum d'utilisations (0 = illimité)"} />
                  </SimpleItem>
                </GroupItem>

                <GroupItem colCount={2}>
                  <SimpleItem
                    key={"frm-rabais"}
                    dataField="rabais"
                    // editorType="dxNumberBox"
                    // editorOptions={{
                    //   format: {
                    //     formatter: formatValue,
                    //   },
                    //   showClearButton: true,
                    // }}
                    label={{ text: 'Rabais de' }}
                    render={() => (
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <NumberBox
                          value={selectedPromotion.rabais}
                          disabled={mode === Mode.Lecture}
                          format={formatValue}
                          onValueChange={
                            (newValue) => setSelectedDataRow((prev: any) => {
                              return (
                                { ...prev, 'rabais': newValue }
                              )
                            }
                            )
                          }
                        />
                        <span
                          onClick={toggleSymbol}
                          style={{
                            cursor: 'pointer',
                            marginLeft: '10px',
                            fontWeight: 'bold',
                            fontSize: '16px',
                          }}
                        >
                          {selectedPromotion.rabaisFixe === true ? '%' : '$'}
                        </span>
                      </div>
                    )}
                  />


                  {/* <SimpleItem key={"frm-rabais"} dataField={"rabais"}
                  >
                    <Label text={"Rabais de"} />
                  </SimpleItem> */}
                  <SimpleItem
                    key={"frm-maximum-rabais"}
                    dataField="maximumRabais"
                    editorType="dxNumberBox"
                    editorOptions={{
                      format: {
                        formatter: formatCurrency,
                      },
                      // showClearButton: true,
                    }}
                    label={{ text: "Jusqu'à l'occurence de" }}
                  />
                  {/* <SimpleItem key={"frm-maximum-rabais"} dataField={"maximumRabais"}
                  >
                    <Label text={"Jusqu'à l'occurence de"} />
                  </SimpleItem> */}
                </GroupItem>
                <GroupItem colCount={2}>
                  <SimpleItem key={"frm-dureeMin"} dataField={"dureeMin"}
                  >
                    <Label text={"Durée min"} />
                  </SimpleItem>
                  <SimpleItem key={"frm-dureeMax"} dataField={"dureeMax"}
                  >
                    <Label text={"Durée max"} />
                  </SimpleItem>
                </GroupItem>
                <SimpleItem key={"frm-jumelable"} dataField={"jumalable"} editorType={"dxCheckBox"}
                  editorOptions={jumelableOptions}
                >
                  <Label visible={false} />
                </SimpleItem>
                <GroupItem caption={"Notes"}>
                  <SimpleItem key={"frm-note"} dataField={"note"} editorType={"dxTextArea"}
                    editorOptions={noteOptions}>
                    <Label visible={false} />
                  </SimpleItem>
                </GroupItem>
              </GroupItem>
              <GroupItem colSpan={3}>
                <GroupItem caption={"Période(s) de validité de la promotion"}>
                  <DataGrid showBorders={true} allowColumnResizing={true}
                    height={125}

                  // dataSource={selectedPromotion.uniteHorsServices}
                  // allowColumnReordering={false}
                  // filterValue={dataGridFilterValue}
                  // noDataText={"Aucune unité hors service"}
                  // onInitNewRow={(e: any) => {
                  //   e.data.idUnite = selectedPromotion.idUnite;
                  //   e.data.isDeleted = false;
                  // }}
                  // onEditorPreparing={(e: any) => {
                  //   if (e.dataField === "idUniteHorsService") {
                  //     const originalOnValueChanged = e.editorOptions.onValueChanged;
                  //     e.editorOptions.onValueChanged = (args: any) => {
                  //       if (originalOnValueChanged) {
                  //         originalOnValueChanged(args);
                  //       }

                  //       const currentData = e.component.option("dataSource");
                  //       const rowIndex = e.rowIndex;
                  //       const rowData = currentData[rowIndex];

                  //       const updatedData = currentData.filter((_: any, index: any) => index !== rowIndex);
                  //       setSelectedDataRow(prev => ({
                  //         ...prev,
                  //         uniteHorsServices: updatedData
                  //       }));
                  //       setIsFieldModifier(true);
                  //     }
                  //   };
                  // }}
                  // onRowRemoving={async (e) => {
                  //   e.cancel = true;
                  //   // if (e?.data?.idPermisType) {
                  //   const index = selectedPromotion.uniteHorsServices.findIndex(item =>
                  //     item.idUniteHorsService === e.data.idUniteHorsService
                  //   );
                  //   if (index !== -1) {
                  //     const updatedData = [...selectedPromotion.uniteHorsServices];
                  //     updatedData[index] = { ...updatedData[index], isDeleted: true };
                  //     setSelectedDataRow(prev => ({
                  //       ...prev,
                  //       uniteHorsServices: updatedData
                  //     }));
                  //   }
                  //   // }
                  // }}
                  >
                    {/* <Editing
                      mode="row"
                      allowAdding={mode !== Mode.Lecture}
                      allowDeleting={mode !== Mode.Lecture}
                      allowUpdating={mode !== Mode.Lecture}
                    /> */}
                    <Column caption={"Date début"} />
                    <Column caption={"Date fin"} />
                    <Column width={75} caption={"Exclure"} />
                  </DataGrid>
                </GroupItem>
                <GroupItem caption={"Période(s) durant laquelle la promotion s'applique"}>
                  <DataGrid
                    showBorders={true}
                    allowColumnResizing={true}
                    dataSource={selectedPromotion.promotionPeriodes}
                    allowColumnReordering={false}
                    filterValue={dataGridFilterValue}
                    noDataText={"Aucune unité hors service"}
                    onInitNewRow={(e: any) => {
                      e.data.idPromotion = selectedPromotion.idPromotion;
                      e.data.isDeleted = false;
                    }}
                    onEditorPreparing={(e: any) => {
                      const originalOnValueChanged = e.editorOptions.onValueChanged;
                      e.editorOptions.onValueChanged = (args: any) => {
                        if (originalOnValueChanged) {
                          originalOnValueChanged(args);
                        }

                        const currentData = e.component.option("dataSource");
                        const rowIndex = e.rowIndex;
                        const rowData = currentData[rowIndex];

                        const updatedData = currentData.filter((_: any, index: any) => index !== rowIndex);
                        setSelectedDataRow(prev => ({
                          ...prev,
                          promotionPeriodes: updatedData
                        }));
                        setIsFieldModifier(true);
                      }
                    }}
                    onRowRemoving={async (e) => {
                      e.cancel = true;
                      // if (e?.data?.idPermisType) {
                      const index = selectedPromotion.promotionPeriodes.findIndex(item =>
                        item.IdPromotionPeriode?.equals(e.data.IdPromotionPeriode)
                      );
                      if (index !== -1) {
                        const updatedData = [...selectedPromotion.promotionPeriodes];
                        updatedData[index] = { ...updatedData[index], isDeleted: true };
                        setSelectedDataRow(prev => ({
                          ...prev,
                          promotionPeriodes: updatedData
                        }));
                      }
                      // }
                    }}
                  >
                    <Editing
                      mode="cell"
                      allowUpdating={mode !== Mode.Lecture}
                      allowDeleting={mode !== Mode.Lecture}
                      allowAdding={mode !== Mode.Lecture}
                      newRowPosition={"first"}
                      startEditAction={"click"}
                    />
                    <Column dataField="debut" dataType="date" caption={"Date début"} />
                    <Column dataField="fin" dataType="date" caption={"Date fin"} />
                    <Column dataField="jours" dataType="boolean" caption={"Lun."} />
                    <Column dataField="jours" dataType="boolean" caption={"Mar."} />
                    <Column dataField="jours" dataType="boolean" caption={"Mer."} />
                    <Column dataField="jours" dataType="boolean" caption={"Jeu."} />
                    <Column dataField="jours" dataType="boolean" caption={"Ven."} />
                    <Column dataField="jours" dataType="boolean" caption={"Sam."} />
                    <Column dataField="jours" dataType="boolean" caption={"Dim."} />
                    <Column dataField="exclusion" dataType="boolean" caption={"Exclure"} />
                    <Column dataField="isDeleted" dataType="boolean" visible={false} />
                  </DataGrid>
                </GroupItem>
              </GroupItem>
            </GroupItem>
          </Form>
        </TabItem>
        <TabItem title="Conditions">
          <Form readOnly={mode === Mode.Lecture} alignItemLabelsInAllGroups={true} formData={selectedPromotion || {} as IPromotion} className={"forms"} labelLocation={"left"} showColonAfterLabel={true}>
            <GroupItem>
              {/* <PopupGestionClients>
                <GestionClient isModeSelection={true} />
              </PopupGestionClients>
              {PopupGestionCategoriesClient && <PopupGestionCategoriesClient>
                <GestionCategoriesClients isModeSelection={true} />
              </PopupGestionCategoriesClient>} */}
              <DataGrid
                // dataSource={conditionsDataSource.dataSource}
                showBorders={true}
                allowColumnResizing={true}
                dataSource={selectedPromotion.conditions}
                allowColumnReordering={false}
                filterValue={dataGridFilterValue}
                noDataText={"Aucune unité hors service"}
                onInitNewRow={(e: any) => {
                  e.data.idPromotion = selectedPromotion.idPromotion;
                  e.data.isDeleted = false;
                }}
                onEditorPreparing={(e: any) => {
                  const originalOnValueChanged = e.editorOptions.onValueChanged;
                  e.editorOptions.onValueChanged = (args: any) => {
                    if (originalOnValueChanged) {
                      originalOnValueChanged(args);
                    }

                    const currentData = e.component.option("dataSource");
                    const rowIndex = e.rowIndex;
                    const rowData = currentData[rowIndex];

                    const updatedData = currentData.filter((_: any, index: any) => index !== rowIndex);
                    setSelectedDataRow(prev => ({
                      ...prev,
                      conditions: updatedData
                    }));
                    setIsFieldModifier(true);
                  }
                }}
                onRowRemoving={async (e) => {
                  e.cancel = true;
                  // if (e?.data?.idPermisType) {
                  const index = selectedPromotion.conditions.findIndex(item =>
                    item.idPromotionCondition?.equals(e.data.idPromotionCondition)
                  );
                  if (index !== -1) {
                    const updatedData = [...selectedPromotion.conditions];
                    updatedData[index] = { ...updatedData[index], isDeleted: true };
                    setSelectedDataRow(prev => ({
                      ...prev,
                      conditions: updatedData
                    }));
                  }
                  // }
                }}
              >
                <Editing
                  mode="cell"
                  allowUpdating={mode !== Mode.Lecture}
                  allowDeleting={mode !== Mode.Lecture}
                  allowAdding={mode !== Mode.Lecture}
                  newRowPosition={"first"}
                  startEditAction={"click"}
                />
                {/* <Column caption={"Type"} editorOptions={categoriesClientEditorOption} /> */}
                <Column caption={"Type"}
                  dataType='string'
                  dataField='typeAuxiliaire'
                  editorOptions={typeEditorOptions}>
                  <Lookup {...typeEditorOptions} />
                </Column>
                <Column dataField="idCategorieClient" dataType="string" alignment="left" caption={"Item"}>
                  <Lookup {...categoriesClientEditorOption} />
                </Column>
                <Column dataType='number' dataField='quantite' caption={"Quantité de"} />
                <Column dataType='number' dataField='quantiteA' caption={"Quantité à"} />
                <Column dataType='string' dataField='conditionEtOu' width={75} caption={"Condition"} editorOptions={conditionEditorOptions} />
                <Column dataType='boolean' dataField='Exclusion' width={60} caption={"Exclus"} />
                <Column dataField="isDeleted" dataType="boolean" visible={false} />
              </DataGrid>
            </GroupItem>
          </Form>
        </TabItem>
        <TabItem title="Cible">
          <Form readOnly={mode === Mode.Lecture} alignItemLabelsInAllGroups={true} formData={selectedPromotion || {} as IPromotion} className={"forms"} labelLocation={"left"} showColonAfterLabel={true}>
            <GroupItem>
              <PopupGestionCatalogueProduits
                onSelectionchanged={(data: any) => {
                  setIsFieldModifier(true);
                  setSelectedDataRow(prev => ({ ...prev, idAuxiliaire: data.idProduit }));
                  formRef.current?.instance().updateData("idAuxiliaire", data.idProduit);
                }}
              >
                <GestionCatalogueProduits isModeSelection={true} />
              </PopupGestionCatalogueProduits>
              <DataGrid
                showBorders={true}
                allowColumnResizing={true}
                dataSource={selectedPromotion.cibles}
                allowColumnReordering={false}
                filterValue={dataGridFilterValue}
                noDataText={""}
                onInitNewRow={(e: any) => {
                  e.data.idPromotion = selectedPromotion.idPromotion;
                  e.data.isDeleted = false;
                }}
                onEditorPreparing={(e: any) => {
                  const originalOnValueChanged = e.editorOptions.onValueChanged;
                  e.editorOptions.onValueChanged = (args: any) => {
                    if (originalOnValueChanged) {
                      originalOnValueChanged(args);
                    }

                    const currentData = e.component.option("dataSource");
                    const rowIndex = e.rowIndex;
                    const rowData = currentData[rowIndex];

                    const updatedData = currentData.filter((_: any, index: any) => index !== rowIndex);
                    setSelectedDataRow(prev => ({
                      ...prev,
                      cibles: updatedData
                    }));
                    setIsFieldModifier(true);
                  }
                }}
                onRowRemoving={async (e) => {
                  e.cancel = true;
                  // if (e?.data?.idPermisType) {
                  const index = selectedPromotion.cibles.findIndex(item =>
                    item.idPromotionCible?.equals(e.data.idPromotionCible)
                  );
                  if (index !== -1) {
                    const updatedData = [...selectedPromotion.cibles];
                    updatedData[index] = { ...updatedData[index], isDeleted: true };
                    setSelectedDataRow(prev => ({
                      ...prev,
                      cibles: updatedData
                    }));
                  }
                  // }
                }}
              >
                <Editing
                  mode="cell"
                  allowUpdating={mode !== Mode.Lecture}
                  allowDeleting={mode !== Mode.Lecture}
                  allowAdding={mode !== Mode.Lecture}
                  newRowPosition={"first"}
                  startEditAction={"click"}
                />
                <Column alignment='left' dataField="typeRabais"
                  dataType="number" caption={"Type"}
                  editorOptions={typeRabaisOptions}>
                  <Lookup {...typeRabaisOptions} />
                </Column >
                <Column alignment='left' dataField="idAuxiliaire" dataType="string" caption={"Item"}
                  editorOptions={catalogueProduitsEditorOption}>
                  <Lookup {...catalogueProduitsEditorOption} />
                </Column>
                <Column dataField="ajoutSiNonPresent" dataType="boolean" caption={"Ajouter si non présent"} />
                <Column dataField="quantite" dataType="number" caption={"Quantité"}
                  // editorOptions={quantiteEditorOptions}
                  editorOptions={{
                    format: { type: "fixedPoint", precision: 2 },
                    valueChangeEvent: "input",
                    elementAttr: { style: "text-align: right;" },
                    customizeText: (e: any) => `${e.value?.toLocaleString("fr-FR")} $`
                  }}
                />
                <Column dataField="isDeleted" dataType="boolean" visible={false} />
              </DataGrid>
            </GroupItem>
          </Form>
        </TabItem>
        <TabItem title="Internet">
          <Form readOnly={mode === Mode.Lecture} alignItemLabelsInAllGroups={true} formData={selectedPromotion || {} as IPromotion} className={"forms"} labelLocation={"left"} showColonAfterLabel={true}>
            <GroupItem colCount={3}>
              <SimpleItem key={"frm-disponible-en-ligne"} dataField={"disponibleEnLigne"} editorType={"dxCheckBox"}
                editorOptions={disponibleEnLigneOptions}
              >
                <Label visible={false} />
              </SimpleItem>
              <SimpleItem key={"frm-automatique"} dataField={"automatique"} editorType={"dxCheckBox"}
                editorOptions={automatiqueOptions}
              >
                <Label visible={false} />
              </SimpleItem>
            </GroupItem>
            <GroupItem caption={"Explications pour le client"}>
              <SimpleItem key={"frm-explication-fr"} dataField={"explicationFr"}
                editorType={"dxTextArea"} editorOptions={explicationFrOptions}
              >
                <Label text={"Français"} location={"top"} />
              </SimpleItem>
              <SimpleItem key={"frm-explication-en"} dataField={"explicationEn"}
                editorType={"dxTextArea"} editorOptions={explicationOptions}
              >
                <Label text={"Anglais"} location={"top"} />
              </SimpleItem>
            </GroupItem>
          </Form>
        </TabItem>
        <TabItem title="Système"><FormSysteme formData={selectedPromotion || {} as IPromotion} /></TabItem>
      </FormListBaseTab>
    </FormListBase>
  );
}

function setDataRow(arg0: string) {
  throw new Error('Function not implemented.');
}